/**
 * UrlParametersSection Component
 *
 * Accordion section for defining URL parameters captured from query string.
 * Part of the "DEFINE → USE" pattern: defines parameters that can be used in destinations.
 *
 * Features:
 * - Autocomplete with common UTM parameters (utm_source, gclid, etc.)
 * - Custom parameter name support
 * - Auto-suggests internal key based on URL param name
 *
 * @module webplacements/components/editors/sources
 */

import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tooltip,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { ParameterDefinition } from '@modules/webplacements/types/webplacements.types';
import { COMMON_URL_PARAMS } from '@modules/webplacements/types/webplacements.types';

export interface UrlParametersSectionProps {
  /** Current URL parameters (filtered from all parameters) */
  parameters: ParameterDefinition[];
  /** Called when parameters change */
  onChange: (params: ParameterDefinition[]) => void;
  /** Whether the editor is disabled */
  disabled?: boolean;
}

interface NewParamState {
  urlParam: string;
  key: string;
}

const initialNewParam: NewParamState = {
  urlParam: '',
  key: '',
};

/**
 * UrlParametersSection Component
 *
 * Provides a table-based interface for defining URL parameters.
 * Each row maps a URL query parameter to an internal key.
 */
export const UrlParametersSection: React.FC<UrlParametersSectionProps> = ({
  parameters,
  onChange,
  disabled = false,
}) => {
  const theme = useTheme();
  const [newParam, setNewParam] = useState<NewParamState>(initialNewParam);
  const [isAdding, setIsAdding] = useState(false);

  const handleAddClick = useCallback(() => {
    setIsAdding(true);
    setNewParam(initialNewParam);
  }, []);

  const handleCancelAdd = useCallback(() => {
    setIsAdding(false);
    setNewParam(initialNewParam);
  }, []);

  const handleSaveNew = useCallback(() => {
    if (!newParam.urlParam || !newParam.key) return;

    // Check for duplicate keys
    if (parameters.some((p) => p.key === newParam.key)) {
      return; // Could show error toast here
    }

    const newDefinition: ParameterDefinition = {
      key: newParam.key,
      source: 'url',
      sourceDetails: {
        urlParam: newParam.urlParam,
      },
      label: COMMON_URL_PARAMS.find((p) => p.param === newParam.urlParam)?.label || newParam.urlParam,
      dataType: 'string',
    };

    onChange([...parameters, newDefinition]);
    setIsAdding(false);
    setNewParam(initialNewParam);
  }, [newParam, parameters, onChange]);

  const handleDelete = useCallback(
    (key: string) => {
      onChange(parameters.filter((p) => p.key !== key));
    },
    [parameters, onChange]
  );

  /**
   * Handle selection from dropdown - auto-suggest key only when URL param actually changes
   *
   * IMPORTANT: With freeSolo mode, onChange fires on blur even if value didn't change.
   * We must check if urlParam actually changed to avoid overwriting key edits.
   */
  const handleUrlParamSelect = useCallback((value: string | null) => {
    if (!value) return;

    setNewParam((prev) => {
      // If URL param didn't change, don't update anything (preserves key edits)
      if (prev.urlParam === value) {
        return prev;
      }

      // URL param changed - auto-suggest key only if key is empty
      const suggestedKey = value.replace(/^utm_/, '').replace(/[^a-zA-Z0-9_]/g, '');
      return {
        urlParam: value,
        key: prev.key || suggestedKey,
      };
    });
  }, []);

  return (
    <Box>
      {/* Header with Add button on the right */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <LinkIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
        <Typography variant="body2" color="text.secondary">
          Capture parameters from URL query string (e.g., ?utm_source=facebook)
        </Typography>
        <Tooltip title="URL parameters are automatically captured when users visit your funnel via a link with query parameters. Common use cases: UTM tracking, referral codes, campaign IDs.">
          <InfoOutlinedIcon fontSize="small" color="action" />
        </Tooltip>
        {/* Spacer to push button to the right */}
        <Box sx={{ flex: 1 }} />
        {!isAdding && (
          <Button
            data-testid="wp-url-add-btn"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
            disabled={disabled}
          >
            Add URL Parameter
          </Button>
        )}
      </Box>

      {/* Parameters Table */}
      {parameters.length > 0 && (
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: '45%' }}>URL Parameter</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '45%' }}>Internal Key</TableCell>
                <TableCell align="right" sx={{ width: '10%' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {parameters.map((param, index) => (
                <TableRow key={param.key} hover data-testid={`wp-url-row-${index}`}>
                  <TableCell>
                    <Chip
                      label={param.sourceDetails.urlParam}
                      size="small"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        bgcolor: alpha(theme.palette.primary.main, 0.15),
                        color: theme.palette.primary.main,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`{{${param.key}}}`}
                      size="small"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        bgcolor: theme.palette.action.selected,
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      data-testid={`wp-url-delete-${index}`}
                      size="small"
                      onClick={() => handleDelete(param.key)}
                      disabled={disabled}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add New Row */}
      {isAdding ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            p: 2,
            bgcolor: theme.palette.action.hover,
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          {/* Vertical layout - one field per row */}
          <Autocomplete
            freeSolo
            options={COMMON_URL_PARAMS.map((p) => p.param)}
            value={newParam.urlParam}
            onChange={(_, value) => handleUrlParamSelect(value as string)}
            inputValue={newParam.urlParam}
            onInputChange={(_, value, reason) => {
              // Update input value on typing, selection fires onChange separately
              if (reason === 'input' || reason === 'reset') {
                setNewParam((prev) => ({ ...prev, urlParam: value }));
              }
            }}
            size="small"
            fullWidth
            renderInput={(params) => (
              <TextField
                {...params}
                data-testid="wp-url-param-input"
                label="URL Parameter"
                placeholder="utm_source"
                helperText="The query parameter to capture"
              />
            )}
          />
          <TextField
            data-testid="wp-url-key-input"
            label="Internal Key"
            value={newParam.key}
            onChange={(e) =>
              setNewParam((prev) => ({
                ...prev,
                key: e.target.value.replace(/[^a-zA-Z0-9_]/g, ''),
              }))
            }
            size="small"
            fullWidth
            required
            error={!newParam.key && newParam.urlParam.length > 0}
            placeholder="source"
            helperText="Used as {{key}} in destinations"
            InputProps={{
              sx: { fontFamily: 'monospace' },
            }}
          />

          {/* Action buttons */}
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button data-testid="wp-url-cancel-btn" size="small" onClick={handleCancelAdd}>
              Cancel
            </Button>
            <Button
              data-testid="wp-url-save-btn"
              size="small"
              variant="contained"
              onClick={handleSaveNew}
              disabled={!newParam.urlParam || !newParam.key}
            >
              Add
            </Button>
          </Box>
        </Box>
      ) : null}

      {/* Empty State */}
      {parameters.length === 0 && !isAdding && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            textAlign: 'center',
            bgcolor: theme.palette.action.hover,
            borderRadius: 1,
            border: `1px dashed ${theme.palette.divider}`,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No URL parameters defined yet.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Add parameters like utm_source, gclid, or custom query params.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default UrlParametersSection;
